import React, { useState } from 'react';
import Swal from 'sweetalert2';

const ConsultationModal = ({ isOpen, onClose, type = 'time' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    county: '',
    message: '',
    file: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData(prev => ({ ...prev, file: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.county || !formData.message) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in all required fields.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      Swal.fire({
        icon: 'success',
        title: 'Consultation Requested!',
        text: 'Thank you! Kehosa Election Consultancy Agency experts will contact you within 24 hours to begin crafting your victory strategy.',
        confirmButtonColor: '#2563eb'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        county: '',
        message: '',
        file: null
      });
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-6xl bg-white rounded-2xl sm:rounded-[32px] shadow-2xl overflow-hidden animate-fade-in max-h-[95vh] sm:max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-secondary-500 hover:text-secondary-800 bg-white/90 hover:bg-white rounded-full p-2 shadow-sm transition-colors duration-200 z-20"
          aria-label="Close consultation modal"
        >
          <i className="bi bi-x-lg text-lg sm:text-xl"></i>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 max-h-full overflow-hidden">
          {/* Left panel */}
          <div className="bg-gradient-to-b from-slate-50 to-slate-100 p-6 sm:p-8 lg:p-12 flex flex-col gap-4 sm:gap-6 custom-scrollbar overflow-y-auto">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-secondary-900 leading-tight">
                Secure Your Victory in Kenya's Elections
              </h2>
              <p className="text-secondary-600 mt-2 sm:mt-3 leading-relaxed text-sm sm:text-base">
                Get expert election strategy from Kehosa Election Consultancy Agency - Kenya's premier political consultants.
                We'll analyze your constituency, build grassroots support, and execute
                a data-driven campaign that resonates with Kenyan voters.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-6 text-secondary-800">
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-primary-700">15+</div>
                <p className="text-xs sm:text-sm text-secondary-600">Election Cycles</p>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-primary-700">200+</div>
                <p className="text-xs sm:text-sm text-secondary-600">Winning Campaigns</p>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-primary-700">47</div>
                <p className="text-xs sm:text-sm text-secondary-600">Counties Covered</p>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-primary-700">87%</div>
                <p className="text-xs sm:text-sm text-secondary-600">Election Success Rate</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 text-secondary-800">
              <div className="flex items-baseline">
                <span className="text-2xl sm:text-3xl font-bold text-primary-700">4.9</span>
                <span className="text-xs sm:text-sm text-secondary-500 ml-1">/5</span>
              </div>
              <div className="flex space-x-1 text-amber-400 text-lg sm:text-xl">
                {[1, 2, 3, 4, 5].map(i => (
                  <i key={i} className="bi bi-star-fill"></i>
                ))}
              </div>
              <span className="text-xs sm:text-sm text-secondary-500 hidden sm:inline">Trusted by politicians</span>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-secondary-500 mb-2">Trusted By Kenya's Leaders</p>
              <div className="flex flex-wrap gap-2 sm:gap-4 text-secondary-400 text-xs sm:text-sm font-semibold">
                <span>Governors</span>
                <span>Senators</span>
                <span>Women Reps</span>
                <span>Party Chairpersons</span>
                <span>County Executives</span>
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="bg-[#d8ccff] p-4 sm:p-6 lg:p-10 relative custom-scrollbar overflow-y-auto max-h-[60vh] lg:max-h-none">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm font-semibold text-secondary-800">
                Your campaign strategy will be kept strictly confidential <span className="font-normal text-secondary-600">under our professional discretion.</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="relative">
                  <i className="bi bi-person text-secondary-400 absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-sm sm:text-base"></i>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Full Name*"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl bg-white border border-transparent focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400 text-sm sm:text-base"
                  />
                </div>
                <div className="relative">
                  <i className="bi bi-telephone text-secondary-400 absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-sm sm:text-base"></i>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number*"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl bg-white border border-transparent focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400 text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="relative">
                  <i className="bi bi-envelope text-secondary-400 absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-sm sm:text-base"></i>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Email Address*"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl bg-white border border-transparent focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400 text-sm sm:text-base"
                  />
                </div>
                <div className="relative">
                  <i className="bi bi-geo-alt text-secondary-400 absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-sm sm:text-base"></i>
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    placeholder="County/Constituency*"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl bg-white border border-transparent focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400 text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="relative">
                <i className="bi bi-lightbulb text-secondary-400 absolute left-3 sm:left-4 top-3 sm:top-4 text-sm sm:text-base"></i>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe your political experience, constituency challenges, and campaign objectives*"
                  className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-2xl bg-white border border-transparent focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400 text-sm sm:text-base resize-none"
                  required
                ></textarea>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-3 bg-white rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-transparent focus-within:ring-2 focus-within:ring-primary-500">
                <i className="bi bi-paperclip text-secondary-400 text-sm sm:text-base"></i>
                <input
                  type="file"
                  name="file"
                  onChange={handleInputChange}
                  className="text-xs sm:text-sm text-secondary-600 file:mr-2 sm:file:mr-4 file:py-1 file:px-2 sm:file:px-3 file:rounded-md file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:text-xs sm:file:text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <i className="bi bi-arrow-repeat animate-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  <span className="text-center">Launch My Election Campaign</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationModal;